import { sendGAEvent, sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';

import type { ListingsCarouselProduct } from 'src/adapters/listingsAPIToProductCarouselAdapter';
import { getOptionsBreakdown } from 'src/redux/basket/helpers/selectorHelpers';
import type { SelectedOptionValuesRecord } from 'src/redux/basket/types';
import { generateProductsForRecommendationsEvent } from 'src/utils/googleAnalytics';
import type {
  ProductClickEvent,
  ProductImpressionsInitialEvent,
  ProductShelfImpressionEvent,
  ProductShelfNavigationImpression,
} from './types';
import { Placement } from './types';

const eventCategory = 'Listings Recommendations';

export const getPlacementTypeFromServingConfig = (
  servingConfig: RecommendationsServingConfig,
): Placement => {
  switch (servingConfig) {
    case 'pdp_similar_products':
      return Placement.SimilarProducts;
    case 'pdp_same_seller_2':
      return Placement.SameSeller2;
    case 'pdp-similar-products-bury-sale':
      return Placement.SimilarProductsSaleBury;
    default:
      throw new Error(`Unknown serving config: ${servingConfig}`);
  }
};

interface TrackRecommendedProductsImpression {
  products: ListingsCarouselProduct[] | null;
  selectedOptionValues: SelectedOptionValuesRecord;
  servingConfig: RecommendationsServingConfig;
}

export const trackRecommendedProductsImpression = ({
  products,
  selectedOptionValues,
  servingConfig,
}: TrackRecommendedProductsImpression) => {
  if (!products) {
    return;
  }

  sendGAEvent<ProductImpressionsInitialEvent>({
    ecommerce: null,
  });

  const placementType = getPlacementTypeFromServingConfig(servingConfig);
  const eventLabel = `${servingConfig || ''} : ${placementType}`;

  const { amountAdded: addedOptionsCost, amountRemoved: removedOptionsCost } = getOptionsBreakdown(
    selectedOptionValues,
    'GBP',
  );

  sendGAEvent<ProductShelfImpressionEvent>({
    event: 'product_impressions',
    event_category: eventCategory,
    event_action: 'Product Impressions',
    event_label: eventLabel,
    ecommerce: {
      impressions: generateProductsForRecommendationsEvent({
        products: products,
        placementName: placementType,
        placementStrategy: servingConfig,
        optionsTotalAmount: addedOptionsCost - removedOptionsCost,
      }),
    },
  });
};

interface TrackRecommendedProductClick {
  product: ListingsCarouselProduct | null;
  productIndex: number;
  selectedOptionValues: SelectedOptionValuesRecord;
  servingConfig: RecommendationsServingConfig;
}
export const trackRecommendedProductClick = (
  e: React.MouseEvent,
  { product, productIndex, selectedOptionValues, servingConfig }: TrackRecommendedProductClick,
) => {
  if (!product) {
    return;
  }

  sendGAEvent<ProductImpressionsInitialEvent>({
    ecommerce: null,
  });

  const placementType = getPlacementTypeFromServingConfig(servingConfig);
  const positionInList = productIndex + 1;
  const eventLabel = `${positionInList} : ${servingConfig || ''} : ${placementType}`;

  const { amountAdded: addedOptionsCost, amountRemoved: removedOptionsCost } = getOptionsBreakdown(
    selectedOptionValues,
    'GBP',
  );

  sendGALinkClickEvent<ProductClickEvent>(e, {
    event: 'product_clicks',
    event_category: eventCategory,
    event_action: 'Product Click',
    event_label: eventLabel,
    ecommerce: {
      click: {
        actionField: {
          list: `product - listings recommendations - ${placementType} - ${servingConfig}`,
        },
        products: generateProductsForRecommendationsEvent({
          products: [product!],
          placementName: placementType,
          placementStrategy: servingConfig,
          positionInList,
          optionsTotalAmount: addedOptionsCost - removedOptionsCost,
        }),
      },
    },
  });
};

interface TrackRecommendedProductsCarouselNavigation {
  navigation: {
    direction: 'Left' | 'Right';
    type: 'Arrow Click' | 'Scroll';
  };
  servingConfig: RecommendationsServingConfig;
}

export const trackRecommendedProductsCarouselNavigation = ({
  navigation,
  servingConfig,
}: TrackRecommendedProductsCarouselNavigation) => {
  const placementType = getPlacementTypeFromServingConfig(servingConfig);
  const eventLabel = `${servingConfig} : ${placementType}`;
  const { direction, type } = navigation;

  const navigationType = type === 'Arrow Click' ? 'Arrow click' : 'Swipe';
  const navigationDirection = direction === 'Right' ? 'next' : 'previous';

  sendGAEvent<ProductShelfNavigationImpression>({
    event: 'custom_event',
    event_category: 'Listings Recommendations',
    event_action: `${navigationType} ${navigationDirection}`,
    event_label: eventLabel,
  });
};
